import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CircularProgress,
  Stack,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import JumboListNoDataPlaceHolder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import JumboSearch from "@jumbo/components/JumboSearch";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import AddBoxIcon from "@mui/icons-material/AddBox";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import BindingItem from "./BindingItem";
import { getBindings, deleteBinding } from "app/services/api/bindings";

const DSOKBindings = () => {
  const { t } = useTranslation();
  const { getAuthUser } = useJumboAuth();
  const { customer } = getAuthUser();
  const defaultLanguage = React.useMemo(() => {
    const langs = customer?.dsokSettings?.customer?.languages;
    return Array.isArray(langs) && langs.length ? langs[0] : "sk";
  }, [customer]);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { showDialog, hideDialog } = useJumboDialog();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [bindingsState, setBindingsState] = useState([]);

  useEffect(() => {
    const fetchBindings = async () => {
      try {
        setLoading(true);
        const productBindings = await getBindings();
        setBindingsState(Array.isArray(productBindings) ? productBindings : []);
      } catch (e) {
        setBindingsState([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBindings();
  }, []);

  const bindings = useMemo(() => {
    const list = Array.isArray(bindingsState) ? bindingsState : [];
    if (!search) return list;
    const s = search.toLowerCase();
    return list.filter((b) =>
      (b.bindingName?.[defaultLanguage] || "").toLowerCase().includes(s)
    );
  }, [search, bindingsState]);

  const handleDelete = (binding) => {
    showDialog({
      variant: "confirm",
      title: t("widgets.confirmDialog.areYouSure", {
        defaultValue: "Are you sure?",
      }),
      onYes: async () => {
        hideDialog();
        try {
          await deleteBinding(binding._id);
          setBindingsState((prev) =>
            (prev || []).filter((b) => b._id !== binding._id)
          );
          enqueueSnackbar(
            `${t("buttons.delete", { defaultValue: "Delete" })} ${t(
              "notifications.wasSuccessful",
              { defaultValue: "was successful" }
            )}`,
            { variant: "success" }
          );
        } catch (error) {
          enqueueSnackbar(
            t("notifications.deleteFailed", {
              defaultValue: "Delete failed",
            }),
            { variant: "error" }
          );
        }
      },
      onNo: () => hideDialog(),
    });
  };

  return (
    <React.Fragment>
      <Typography variant={"h2"} mb={3}>
        {t("pages.dsokBindings.title", { defaultValue: "Bindings" })}
      </Typography>
      <Stack direction="row" alignItems="center" mb={2} spacing={1}>
        <Card sx={{ mb: 0, width: "50%" }}>
          <JumboSearch
            page="DSOKBindings"
            onChange={setSearch}
            sx={{ width: "100%" }}
            placeholder={t("pages.dsokBindings.searchPlaceholder", {
              defaultValue: "Search bindings...",
            })}
          />
        </Card>
        <Tooltip
          title={t("pages.dsokBindings.addBinding", {
            defaultValue: "Add Binding",
          })}
        >
          <IconButton onClick={() => navigate("/customer/dsok/bindings/new")}>
            <AddBoxIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      {loading ? (
        <CircularProgress sx={{ m: "5px 50%" }} />
      ) : bindings?.length ? (
        <div>
          {(bindings || []).map((binding) => (
            <BindingItem
              key={binding._id}
              item={binding}
              disabled={loading}
              onEdit={() => navigate(`/customer/dsok/bindings/${binding._id}`)}
              onDelete={handleDelete}
            />
          ))}
        </div>
      ) : (
        <JumboListNoDataPlaceHolder />
      )}
    </React.Fragment>
  );
};

export default DSOKBindings;
